import { Alert, Button, Field, Modal, TextInput } from "@nabble/ui";
import { useMemo } from "react";
import type { Permission } from "../../../api/adminPermissions";
import { ADMIN_ROLE_ID, COLLABORATOR_ROLE_ID, type Role } from "../../../api/adminRoles";
import { PermissionGroupList } from "./PermissionGroupList";
import { ADMIN_CENTER_PERMISSION, groupPermissions } from "./permissionGroups";
import { useRoleForm } from "./useRoleForm";

interface RoleDialogProps {
  /** The role to view or edit; null to create one. */
  role: Role | null;
  permissions: Permission[];
  onClose: () => void;
  onSaved: () => void;
}

function roleDescription(role: Role | null): string {
  if (role?.id === ADMIN_ROLE_ID) return "Base role: administrators always hold every permission, including the Admin Center.";
  if (role?.id === COLLABORATOR_ROLE_ID) return "Base role for everyday work. Choose what collaborators can do.";
  return "People hold it in the organizations where it is given to them, alone or with other roles. Roles with permissions over the whole suite can only be given by suite administrators.";
}

export function RoleDialog({ role, permissions, onClose, onSaved }: RoleDialogProps): React.JSX.Element {
  // Administrator: always every permission. Collaborator: fixed name, permissions chosen here.
  const readOnly = role?.id === ADMIN_ROLE_ID;
  const nameFixed = role?.system ?? false;
  const isCollaborator = role?.id === COLLABORATOR_ROLE_ID;
  const form = useRoleForm(role, onSaved);
  const { selected } = form;

  const grouped = useMemo(
    () => groupPermissions(isCollaborator ? permissions.filter((p) => p.key !== ADMIN_CENTER_PERMISSION) : permissions),
    [permissions, isCollaborator],
  );

  return (
    <Modal
      open
      size="lg"
      icon="key"
      title={role ? role.name : "Create role"}
      description={roleDescription(role)}
      onClose={onClose}
      actions={
        readOnly ? (
          <Button label="Close" variant="secondary" onClick={onClose} />
        ) : (
          <>
            <span className="nb-help" style={{ marginRight: "auto", alignSelf: "center" }}>
              {`${selected.length} ${selected.length === 1 ? "permission selected" : "permissions selected"}`}
            </span>
            <Button label="Cancel" variant="secondary" onClick={onClose} />
            <Button label={role ? "Save changes" : "Create role"} loading={form.loading} onClick={() => void form.submit()} />
          </>
        )
      }
    >
      <div className="nb-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <Field label="Name" error={form.nameError ?? undefined}>
          <TextInput value={form.name} onChange={(e) => form.setName(e.target.value)} disabled={nameFixed} placeholder="E.g. Data analyst" />
        </Field>
        <Field label="Description" optional={!nameFixed}>
          <TextInput value={form.description} onChange={(e) => form.setDescription(e.target.value)} disabled={nameFixed} placeholder="What this role is for" />
        </Field>
      </div>

      <div>
        <div className="nb-row-between" style={{ marginBottom: "var(--space-3)" }}>
          <span className="nb-label">Permissions by module</span>
        </div>
        {grouped.length === 0 ? <p className="nb-help">No permissions available: no installed module declares any.</p> : null}
        {grouped.map((group) => (
          <PermissionGroupList key={group[0]} group={group} selected={selected} readOnly={readOnly} onToggle={form.togglePermissions} />
        ))}
      </div>
      {form.error ? <Alert tone="danger">{form.error}</Alert> : null}
    </Modal>
  );
}
